import { StandardEvents, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { ThemeEvents } from '@theme/events';
import { limitedCartFetch } from '@theme/gimoka-cart-limit';

class GimokaStockUp extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    if (this.dataset.mode === 'mix') return this.connectMix(signal);
    this.addEventListener('change', this.onChange, { signal });
    this.addEventListener('click', this.onFlavourClick, { signal });
    this.addEventListener('keydown', this.onFlavourKeydown, { signal });
    this.addEventListener('keyup', this.onFlavourKeyup, { signal });
    this.addEventListener('focusout', this.onFlavourFocusout, { signal });
    document.addEventListener('pointerdown', this.onOutsidePointer, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.refreshFromCart, { signal });
    if (this.dataset.mode === 'product') {
      const scope = this.closest('.shopify-section');
      scope?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.onQuantity, { signal });
      scope?.addEventListener(StandardEvents.productSelect, this.onVariant, { signal });
      queueMicrotask(() => this.syncProduct());
    } else {
      this.selectVariant();
    }
  }
  disconnectedCallback() { this.controller?.abort(); }
  closeFlavourDropdown(restoreFocus = false) {
    const dropdown = this.querySelector('[data-flavour-dropdown]');
    if (!dropdown?.open) return;
    dropdown.open = false;
    if (restoreFocus) dropdown.querySelector('summary')?.focus({ preventScroll: true });
  }
  onFlavourClick = (event) => {
    if (this.flavourArrowNavigation && event.detail === 0) return;
    if (event.target.matches('[data-flavour-choice]:checked:not(:disabled)')) this.closeFlavourDropdown(true);
  };
  onFlavourKeyup = () => { this.flavourArrowNavigation = false; };
  onFlavourKeydown = (event) => {
    this.flavourArrowNavigation = event.key.startsWith('Arrow') && event.target.matches('[data-flavour-choice]');
    if (!event.target.closest('[data-flavour-dropdown]')) return;
    if (event.key === 'Escape' || (event.key === 'Enter' && event.target.matches('[data-flavour-choice]'))) {
      event.preventDefault();
      this.closeFlavourDropdown(true);
    }
  };
  onOutsidePointer = (event) => {
    const dropdown = this.querySelector('[data-flavour-dropdown]');
    if (dropdown && !dropdown.contains(event.target)) this.closeFlavourDropdown();
  };
  onFlavourFocusout = (event) => {
    const dropdown = this.querySelector('[data-flavour-dropdown]');
    if (dropdown && event.relatedTarget && !dropdown.contains(event.relatedTarget)) this.closeFlavourDropdown();
  };
  get formComponent() { return this.closest('.shopify-section')?.querySelector('product-form-component'); }
  onChange = (event) => {
    if (event.target.matches('[data-flavour-choice]') && event.target.checked && !event.target.disabled) {
      const input = this.querySelector('[data-stock-variant]');
      if (input) input.value = event.target.value;
      return this.selectVariant();
    }
    if (event.target.matches('[data-stock-variant]')) return this.selectVariant();
    if (!event.target.matches('[data-tier-choice]')) return;
    const value = event.target.value;
    if (this.dataset.mode === 'product') {
      const selector = this.formComponent?.querySelector('quantity-selector-component');
      if (selector) {
        selector.setValue(value);
        selector.onQuantityChange();
        selector.updateButtonStates();
        this.syncProduct();
      }
    } else {
      const input = this.querySelector('input[name="quantity"]');
      if (input) input.value = value;
      this.updateHomeButton();
    }
  };
  onQuantity = (event) => {
    if (event.target.closest('product-form-component') === this.formComponent) this.syncProduct();
  };
  syncProduct() {
    const input = this.formComponent?.querySelector('input[name="quantity"]');
    if (!input) return;
    const fieldset = this.querySelector('fieldset');
    if (fieldset) fieldset.hidden = false;
    for (const radio of this.querySelectorAll('[data-tier-choice]')) radio.checked = !radio.disabled && radio.value === input.value;
  }
  onVariant = (event) => {
    if (event.target.closest('product-card')) return;
    this.sequence = (this.sequence || 0) + 1;
    const sequence = this.sequence;
    const cartVersion = this.refreshVersion || 0;
    event.promise.then(({ detail }) => {
      if (!this.isConnected || sequence !== this.sequence || !detail?.html) return;
      const next = [...detail.html.querySelectorAll('gimoka-stock-up[data-mode="product"]')].find(el => el.dataset.blockId === this.dataset.blockId);
      if (!next) return;
      if (cartVersion !== (this.refreshVersion || 0)) {
        const fieldset = this.querySelector('fieldset');
        if (fieldset) fieldset.hidden = true;
        requestAnimationFrame(() => {
          if (sequence === this.sequence && this.isConnected) this.refreshFromCart({ promise: Promise.resolve() });
        });
        return;
      }
      this.replaceChildren(...[...next.childNodes].map(node => node.cloneNode(true)));
      requestAnimationFrame(() => this.syncProduct());
    }).catch(() => {});
  };
  refreshFromCart = async (event) => {
    const version = this.refreshVersion = (this.refreshVersion || 0) + 1;
    try {
      await event.promise;
      if (!this.isConnected) return;
      const section = this.closest('.shopify-section');
      if (!section?.id) return;
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', section.id.replace(/^shopify-section-/, ''));
      const variantId = this.formComponent?.querySelector('[name="id"]')?.value;
      if (this.dataset.mode === 'product' && variantId) url.searchParams.set('variant', variantId);
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) return;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (!this.isConnected || version !== this.refreshVersion) return;
      const next = [...html.querySelectorAll('gimoka-stock-up')].find(el => el.dataset.mode === this.dataset.mode && el.dataset.blockId === this.dataset.blockId);
      if (!next) return;
      if (this.dataset.mode === 'product') {
        if (variantId !== this.formComponent?.querySelector('[name="id"]')?.value) return;
        this.replaceChildren(...[...next.childNodes].map(node => node.cloneNode(true)));
        this.syncProduct();
      } else {
        const quantity = this.querySelector('input[name="quantity"]')?.value;
        for (const panel of this.querySelectorAll('[data-variant-panel]')) {
          const replacement = [...next.querySelectorAll('[data-variant-panel]')].find(el => el.dataset.variantPanel === panel.dataset.variantPanel);
          if (replacement) panel.replaceChildren(...[...replacement.childNodes].map(node => node.cloneNode(true)));
        }
        this.selectVariant(quantity);
      }
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('Gimoka quantity cards could not refresh; Shopify validates stock on add.', error);
    }
  };
  selectVariant(preferredQuantity) {
    const select = this.querySelector('[data-stock-variant]');
    if (!select) return;
    const previous = preferredQuantity || this.querySelector('[data-tier-choice]:checked')?.value;
    for (const panel of this.querySelectorAll('[data-variant-panel]')) {
      const active = panel.dataset.variantPanel === select.value;
      panel.hidden = !active;
      for (const radio of panel.querySelectorAll('[data-tier-choice]')) {
        if (!radio.hasAttribute('data-server-disabled')) radio.dataset.serverDisabled = String(radio.disabled);
        radio.disabled = !active || radio.dataset.serverDisabled === 'true';
        radio.checked = false;
      }
      if (active) {
        const productForm = this.querySelector('product-form-component');
        if (productForm && panel.dataset.productId) productForm.dataset.productId = panel.dataset.productId;
        const options = [...panel.querySelectorAll('[data-tier-choice]:not(:disabled)')];
        const chosen = options.find(r => r.value === previous) || options[0];
        if (chosen) chosen.checked = true;
        this.querySelector('input[name="quantity"]').value = chosen?.value || '1';
      }
    }
    for (const image of this.querySelectorAll('[data-variant-image]')) image.hidden = image.dataset.variantImage !== select.value;
    const summary = this.querySelector('[data-flavour-summary]');
    const choice = [...this.querySelectorAll('[data-flavour-choice]')].find(input => input.value === select.value);
    const content = choice?.closest('label')?.querySelector('.gimoka-flavour__content');
    if (summary && content) summary.replaceChildren(...[...content.childNodes].map(node => node.cloneNode(true)));
    this.updateHomeButton();
  }
  updateHomeButton() {
    const button = this.querySelector('[data-stock-submit]');
    if (button) button.disabled = !this.querySelector('[data-tier-choice]:checked:not(:disabled)');
  }

  // --- Mixed flavour box -----------------------------------------------------
  // One box, several flavours. The Shopify automatic discounts qualify on total
  // quantity rather than on a single flavour, so the picker mirrors that.

  connectMix(signal) {
    this.mixCounts = new Map();
    this.addEventListener('click', this.onMixClick, { signal });
    this.addEventListener('change', this.onMixTierChange, { signal });
    this.addEventListener('submit', this.onMixSubmit, { signal });
    // The element can be upgraded before its children are parsed, so paint once
    // the subtree is definitely in place.
    queueMicrotask(() => {
      if (this.isConnected) this.renderMix();
    });
  }

  // Read lazily, and never cache an empty result: connectedCallback can run
  // before the config script tag exists.
  get mixConfig() {
    if (this.mixConfigCache) return this.mixConfigCache;
    const node = this.querySelector('[data-mix-config]');
    if (!node) return {};
    try {
      this.mixConfigCache = JSON.parse(node.textContent || '{}');
    } catch {
      this.mixConfigCache = {};
    }
    return this.mixConfigCache;
  }

  get mixTarget() {
    const checked = this.querySelector('[data-mix-tier]:checked');
    return Number(checked?.value) || Number(this.dataset.defaultQuantity) || 0;
  }

  get mixChosen() {
    let total = 0;
    for (const count of this.mixCounts.values()) total += count;
    return total;
  }

  mixItems() {
    return [...this.querySelectorAll('[data-mix-item]:not([data-unavailable])')];
  }

  onMixClick = (event) => {
    const clear = event.target.closest('[data-mix-clear]');
    if (clear) {
      this.mixCounts.clear();
      return this.renderMix();
    }
    const step = event.target.closest('[data-mix-step]');
    if (!step) return;
    const item = step.closest('[data-mix-item]');
    if (!item) return;
    const id = item.dataset.variantId;
    const delta = Number(step.dataset.mixStep);
    const next = Math.max(0, (this.mixCounts.get(id) || 0) + delta);
    // Never let one flavour exceed the box, and never exceed the per-product cart cap.
    const ceiling = Math.min(this.mixTarget, 20);
    if (next > ceiling) return;
    if (delta > 0 && this.mixChosen >= this.mixTarget) return;
    if (next === 0) this.mixCounts.delete(id);
    else this.mixCounts.set(id, next);
    this.renderMix();
  };

  onMixTierChange = (event) => {
    if (!event.target.matches('[data-mix-tier]')) return;
    // Shrinking the box trims the biggest piles first so earlier choices survive.
    let overflow = this.mixChosen - this.mixTarget;
    while (overflow > 0) {
      let biggestId = null;
      let biggest = 0;
      for (const [id, count] of this.mixCounts) {
        if (count >= biggest) {
          biggest = count;
          biggestId = id;
        }
      }
      if (!biggestId) break;
      if (biggest <= 1) this.mixCounts.delete(biggestId);
      else this.mixCounts.set(biggestId, biggest - 1);
      overflow -= 1;
    }
    this.renderMix();
  };

  formatMoney(cents) {
    const format = this.mixConfig?.moneyFormat || '{{ amount }}';
    const amount = Number(cents) / 100;
    const withCommas = (value, decimals) =>
      value.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, token) => {
      switch (token) {
        case 'amount_no_decimals':
        case 'amount_no_decimals_with_comma_separator':
          return withCommas(Math.round(amount), 0);
        case 'amount_with_period_and_space_separator':
          return withCommas(amount, 2).replace(/,/g, ' ').replace(/\.(\d{2})$/, ',$1');
        case 'amount_with_apostrophe_separator':
          return withCommas(amount, 2).replace(/,/g, "'");
        default:
          return withCommas(amount, 2);
      }
    });
  }

  renderMix() {
    const target = this.mixTarget;
    const chosen = this.mixChosen;
    const strings = this.mixConfig?.strings || {};

    let subtotal = 0;
    for (const item of this.mixItems()) {
      const id = item.dataset.variantId;
      const count = this.mixCounts.get(id) || 0;
      subtotal += count * Number(item.dataset.price || 0);
      const output = item.querySelector('[data-mix-count]');
      if (output) output.textContent = String(count);
      item.classList.toggle('gimoka-mix__item--chosen', count > 0);
      const minus = item.querySelector('[data-mix-step="-1"]');
      const plus = item.querySelector('[data-mix-step="1"]');
      if (minus) minus.disabled = count === 0;
      if (plus) plus.disabled = chosen >= target || count >= Math.min(target, 20);
    }

    const remaining = target - chosen;
    const bar = this.querySelector('[data-mix-bar]');
    if (bar) bar.style.width = `${target > 0 ? Math.min(100, (chosen / target) * 100) : 0}%`;

    const status = this.querySelector('[data-mix-status]');
    if (status) {
      const progress = (strings.progress || '{{ count }} of {{ total }}')
        .replace('__COUNT__', String(chosen))
        .replace('__TOTAL__', String(target));
      let hint = '';
      if (remaining > 0) hint = (strings.remaining || '').replace('__COUNT__', String(remaining));
      else if (remaining < 0) hint = (strings.over || '').replace('__COUNT__', String(-remaining));
      else hint = strings.complete || '';
      status.textContent = hint ? `${progress} · ${hint}` : progress;
      status.classList.toggle('gimoka-mix__status--complete', remaining === 0 && chosen > 0);
    }

    const totalRow = this.querySelector('[data-mix-total-row]');
    const totalValue = this.querySelector('[data-mix-total]');
    if (totalRow && totalValue) {
      const saving = Number(this.querySelector('[data-mix-tier]:checked')?.dataset.saving || 0);
      // Only claim the offer total once the box is actually full and verified.
      const payable = this.mixConfig?.priced && remaining === 0 ? Math.max(0, subtotal - saving) : subtotal;
      totalValue.textContent = this.formatMoney(payable);
      totalRow.hidden = chosen === 0;
    }

    const summary = this.querySelector('[data-mix-summary]');
    if (summary) {
      const chosenItems = this.mixItems().filter((item) => this.mixCounts.get(item.dataset.variantId));
      summary.replaceChildren(
        ...chosenItems.map((item) => {
          const li = document.createElement('li');
          li.textContent = `${this.mixCounts.get(item.dataset.variantId)} × ${item.dataset.title}`;
          return li;
        })
      );
      summary.hidden = chosenItems.length === 0;
    }

    const clear = this.querySelector('[data-mix-clear]');
    if (clear) clear.hidden = chosen === 0;

    const submit = this.querySelector('[data-mix-submit]');
    if (submit) submit.disabled = chosen === 0 || chosen !== target || this.mixBusy === true;
  }

  setMixError(message) {
    const target = this.querySelector('[data-mix-error]');
    if (!target) return;
    target.textContent = message || '';
    target.classList.toggle('hidden', !message);
  }

  onMixSubmit = async (event) => {
    if (!event.target.matches('[data-mix-form]')) return;
    event.preventDefault();
    if (this.mixBusy) return;
    const target = this.mixTarget;
    if (this.mixChosen !== target || target === 0) return;

    const lines = this.mixItems()
      .filter((item) => this.mixCounts.get(item.dataset.variantId))
      .map((item) => ({
        variantId: item.dataset.variantId,
        productId: item.dataset.productId,
        quantity: this.mixCounts.get(item.dataset.variantId),
      }));
    if (!lines.length) return;

    this.mixBusy = true;
    this.setMixError('');
    this.renderMix();

    // Aggregate per product so the cart guard sees a truthful per-product total.
    const perProduct = new Map();
    for (const line of lines) {
      perProduct.set(line.productId, (perProduct.get(line.productId) || 0) + line.quantity);
    }
    const productTotals = [...perProduct].map(([id, quantity]) => ({ id, quantity }));

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .filter((node) => node instanceof HTMLElement && node.dataset.sectionId)
      .map((node) => node.dataset.sectionId);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((line) => ({ merchandiseId: line.variantId, quantity: line.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await limitedCartFetch(
        Theme.routes.cart_add_url,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: lines.map((line) => ({ id: Number(line.variantId), quantity: line.quantity })),
            sections: sectionIds.join(','),
          }),
        },
        'add',
        productTotals
      );
      const result = await response.json();

      if (result.status) {
        this.setMixError(result.message || this.mixConfig?.strings?.error || '');
        this.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );
        const cart = await this.fetchMixCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, source: 'gimoka-stock-up', didError: true },
        });
        return;
      }

      this.mixCounts.clear();
      const cart = await this.fetchMixCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'gimoka-stock-up',
          itemCount: target,
          sections: result.sections,
          didError: false,
        },
      });
    } catch (error) {
      this.setMixError(this.mixConfig?.strings?.error || '');
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      this.mixBusy = false;
      this.renderMix();
    }
  };

  async fetchMixCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      credentials: 'same-origin',
      cache: 'no-store',
    });
    return response.json();
  }
}
if (!customElements.get('gimoka-stock-up')) customElements.define('gimoka-stock-up', GimokaStockUp);
